import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { signsInWithModal } from '../../util/authPrompt';
import { formatMoney } from '../../util/currency';
import { timestampToDate } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';
import * as log from '../../util/log';

import { Page, LayoutSingleColumn } from '../../components';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './ListingPage.module.css';
import { getDeliveryMethod } from '../../util/dataExtractors';

/**
 * This file contains shared functions from each ListingPage variants.
 */

const { UUID } = sdkTypes;

/**
 * Helper to get formattedPrice and priceTitle for SectionHeading component.
 * @param {Money} price listing's price
 * @param {String} marketplaceCurrency currency of the price (e.g. 'USD')
 * @param {Object} intl React Intl instance
 * @returns Object literal containing formattedPrice and priceTitle
 */
export const priceData = (price, marketplaceCurrency, intl) => {
  if (price && price.currency === marketplaceCurrency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

/**
 * Get category's label.
 *
 * @param {Array} categories array of category objects (key & label)
 * @param {String} value selected category value
 * @returns label for the selected value
 */
export const categoryLabel = (categories, value) => {
  const cat = categories.find(c => c.key === value);
  return cat ? cat.label : value;
};

/**
 * Filter listing images with correct custom image variant name.
 * Used for facebook, twitter and page schema images.
 *
 * @param {Listing} listing
 * @param {String} variantName
 * @returns correct image variant specified by variantName parameter.
 */
export const listingImages = (listing, variantName) =>
  (listing.images || [])
    .map(image => {
      const variants = image.attributes.variants;
      const variant = variants ? variants[variantName] : null;

      // deprecated
      // for backwards combatility only
      const sizes = image.attributes.sizes;
      const size = sizes ? sizes.find(i => i.name === variantName) : null;

      return variant || size;
    })
    .filter(variant => variant != null);

/**
 * Callback for the "contact" button on ListingPage to open inquiry modal.
 *
 * @param {Object} parameters all the info needed to open inquiry modal.
 */
export const handleContactUser = parameters => () => {
  const {
    history,
    params,
    currentUser,
    callSetInitialValues,
    location,
    routes,
    setInitialValues,
    setInquiryModalOpen,
    onRequireAuth,
  } = parameters;

  // Desktop: the sign-in modal over this listing, and the message window once signed in
  if (!currentUser && onRequireAuth && signsInWithModal()) {
    onRequireAuth({ then: () => setInquiryModalOpen(true) });
  } else if (!currentUser) {
    const state = { from: `${location.pathname}${location.search}${location.hash}` };

    // We need to log in before showing the modal, but first we need to ensure
    // that modal does open when user is redirected back to this listingpage
    callSetInitialValues(setInitialValues, { inquiryModalOpenForListingId: params.id });

    // signup and return back to listingPage.
    history.push(createResourceLocatorString('SignupPage', routes, {}, {}), state);
  } else {
    setInquiryModalOpen(true);
  }
};

/**
 * Make an offer on ListingPage. A guest signs up first and comes back to this listing (as
 * Contact seller does); a signed-in buyer gets the offer window.
 *
 * @param {Object} parameters all the info needed to open the offer modal.
 */
export const handleMakeOffer = parameters => () => {
  const { history, currentUser, location, routes, setOffersModalOpen, onRequireAuth } = parameters;

  // Desktop: the sign-in modal over this listing, and the offer window once signed in
  if (!currentUser && onRequireAuth && signsInWithModal()) {
    onRequireAuth({ then: () => setOffersModalOpen(true) });
  } else if (!currentUser) {
    const state = { from: `${location.pathname}${location.search}${location.hash}` };
    history.push(createResourceLocatorString('SignupPage', routes, {}, {}), state);
  } else {
    setOffersModalOpen(true);
  }
};

/**
 * Callback for the inquiry modal to submit aka create inquiry transaction on ListingPage.
 * Note: this is for booking and purchase processes. Inquiry process is handled through handleSubmit.
 *
 * @param {Object} parameters all the info needed to create inquiry.
 */
export const handleSubmitInquiry = parameters => values => {
  const { history, params, getListing, onSendInquiry, routes, setInquiryModalOpen } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);
  const { message } = values;

  onSendInquiry(listing, message.trim())
    .then(txId => {
      setInquiryModalOpen(false);

      // Redirect to OrderDetailsPage
      history.push(createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {}));
    })
    .catch(() => {
      // Ignore, error handling in duck file
    });
};

/**
 * Handle order submit from OrderPanel.
 *
 * @param {Object} parameters all the info needed to redirect user to CheckoutPage.
 */
export const handleSubmit = parameters => values => {
  const {
    history,
    params,
    currentUser,
    getListing,
    from_address,
    cartItems,
    offerParams,
    callSetInitialValues,
    onInitializeCardPaymentData,
    routes,
    author,
    transaction,
    onRequireAuth,
  } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);
  const savedDeliveryMethod = getDeliveryMethod(listing);
  try {
    // Check if listing has an author; otherwise, set it using the `author` parameter
    if (!listing.author) {
      listing.author = author;
    }

    const {
      bookingDates,
      bookingStartTime,
      bookingEndTime,
      bookingStartDate, // not relevant (omit)
      bookingEndDate, // not relevant (omit)
      quantity: quantityRaw,
      deliveryMethod = savedDeliveryMethod,
      ...otherOrderData
    } = values;
    const bookingMaybe = bookingDates
      ? {
          bookingDates: {
            bookingStart: bookingDates.startDate,
            bookingEnd: bookingDates.endDate,
          },
        }
      : bookingStartTime && bookingEndTime
      ? {
          bookingDates: {
            bookingStart: timestampToDate(bookingStartTime),
            bookingEnd: timestampToDate(bookingEndTime),
          },
        }
      : {};
    const quantity = Number.parseInt(quantityRaw, 10);
    // Default to 1 if quantity is not provided (e.g., for oneItem stock type)
    const quantityMaybe = Number.isInteger(quantity) ? { quantity } : { quantity: 1 };
    const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

    // Ensure cartItems reflect the selected quantity (for single-item Buy Now flow)
    // Only then: in a cart of several items each keeps its own quantity, and
    // giving them all the main item's quantity asks for more than is in stock.
    const selectedQty = quantityMaybe.quantity || 1;
    const isSingleItem = (cartItems ?? []).length === 1;
    const patchedCartItems = (cartItems ?? []).map(item =>
      isSingleItem
        ? { ...item, stockCount: item.stockCount > 1 ? item.stockCount : selectedQty }
        : item
    );

    const initialValues = {
      listing,
      // An accepted offer is paid on its own order.
      ...(transaction ? { transaction } : {}),
      orderData: {
        ...bookingMaybe,
        ...quantityMaybe,
        ...deliveryMethodMaybe,
        ...otherOrderData,
        from_address,
        cartItems: patchedCartItems,
        offerParams,
      },
      confirmPaymentError: null,
    };

    const saveToSessionStorage = !currentUser;

    // Customize checkout page state with current listing and selected orderData
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

    callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    const checkoutPath = createResourceLocatorString(
      'CheckoutPage',
      routes,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    );
    // A guest on desktop signs in in the modal over this listing, which keeps its place,
    // and then goes on to checkout (the order is already saved for it)
    if (!currentUser && onRequireAuth && signsInWithModal()) {
      onRequireAuth({ to: checkoutPath });
    } else {
      history.push(checkoutPath);
    }
  } catch (error) {
    // Buy stays where it is; the error is reported rather than lost
    log.error(error, 'checkout-prepare-failed', { listingId: params.id });
  }
};

/**
 * Create fallback views for the ListingPage: LoadingPage and ErrorPage.
 * The PlainPage is just a helper for them.
 */
const PlainPage = props => {
  const { title, topbar, scrollingDisabled, children } = props;
  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={topbar} footer={<FooterContainer />}>
        {children}
      </LayoutSingleColumn>
    </Page>
  );
};

export const ErrorPage = props => {
  const { topbar, scrollingDisabled, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.errorText}>
        <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
      </p>
    </PlainPage>
  );
};

export const LoadingPage = props => {
  const { topbar, scrollingDisabled, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.loadingText}>
        <FormattedMessage id="ListingPage.loadingListingMessage" />
      </p>
    </PlainPage>
  );
};
